/**
 * AI Reviews panel — on-demand (re-)trigger of the loan AI agents.
 *
 * Backend contract: eastview-os frontend_documentation/run-agent-reviews.md +
 * the live main-app router app/routers/agent_reviews.py (prefix /admin/loans,
 * require_admin). The frontend calls these on the MAIN APP (the normal LOS API
 * base) — the main app gates the request, publishes to the listener, and returns
 * immediately. ALL THREE are async from the FE's view:
 *   - allowed → 202 { runId, status:"queued", message } (listener runs the agent
 *     workflow and fires a completion notification: loan_opening_review_complete
 *     / post_uw_qc_review_complete / unexecuted_package_index_complete).
 *   - gated   → 200 { status:"skipped", reason, message } (NOT 202).
 *
 * (The published doc still calls Loan Opening "synchronous with an inline
 * summary" — the live main-app endpoint queues it like the others, so we treat
 * all three uniformly and let the summary ride the completion notification.)
 *
 * Button state is driven by GET .../ai-reviews/status → per-agent
 * { lastRun, canRun, reason, running, run }. Disable Run when canRun is false and
 * surface the reason. Run-gating: Loan Opening runs once per loan; Post-UW QC and
 * Unexecuted-package re-run only when the loan changed since the last run.
 *
 * IN-FLIGHT RUN LOCK (backend 2026-08-25). The status payload carries a
 * SERVER-side `running: true` (+ `run: { runId, startedAt, startedBy }`, and
 * `reason: "running"`) while a triggered run hasn't completed. That flag — not a
 * client-local one — is the in-progress state: a local flag dies on refresh and is
 * invisible to the other users looking at the same loan. A run POST while a run is
 * in flight returns 409 { status:"already_running", runId, startedAt, startedBy,
 * message }, and `force` does NOT bypass it (running is running). The lock
 * releases when the completion record lands, or after the backend's 20-minute
 * stale ceiling if a workflow died. We refetch status on any `*_complete`
 * notification for this loan, and poll while anything is running (the run may have
 * been started by ANOTHER user, whose completion notification never reaches us).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import { keyframes } from "@mui/system";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import { AgGridReact } from "ag-grid-react";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import {
  formatDateTime,
  normalizeIsoStringToUtc,
} from "../../../_src_shared/utils/functions/formatDateTime";
import { useIsAcquisitions } from "../../../pipelines/PipelineContext";

// Layout for the agent list: "table" (AG Grid, current trial) or "cards" (the
// previous responsive card grid). Both renderers are kept below — flip this one
// constant to switch back.
const AGENT_LAYOUT = "table";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

// The triggers. `statusKey` maps to the GET /status payload; `pipeline` says
// which loan pipeline the agent belongs to. The status response tags each agent
// with the same `pipeline` value and is authoritative — origination agents must
// NOT show on acquisitions loans (some write to the preclose collection only).
const REVIEWS = [
  {
    key: "opening",
    statusKey: "loan_opening",
    pipeline: "preclose",
    name: "Loan Opening",
    summary: "Fills blank loan fields from source documents.",
    details:
      "Fills empty loan fields from source documents, appends a processing note, suggests document associations, and refreshes suggested missing documents.",
    // The ONLY agent that writes to the loan itself — every other agent stages
    // findings you have to approve first. Called out loudly in the table.
    writesLoanData: true,
    outcomes: [
      "Adds a note listing every field it filled; all fills are undoable",
      "Suggests placement for unfiled documents and flags missing ones",
      "Updates loan information based on details found in your documents",
    ],
  },
  {
    key: "suggestConditions",
    statusKey: "suggest_conditions",
    pipeline: "preclose",
    name: "Suggested Conditions",
    summary:
      "Generates conditions and missing-document findings for your review.",
    details:
      "Runs the finding emitters (missing documents, stale docs, title, insurance, experience, per-person screening) and stages suggested conditions in the findings queue below for review.",
    outcomes: [
      "Checks for missing or out-of-date documents, title, insurance, and background checks",
      "Nothing reaches the loan until you approve it",
    ],
  },
  {
    key: "hardExceptions",
    statusKey: "hard_exceptions",
    pipeline: "preclose",
    name: "Hard Exception Check",
    summary:
      "Tests the loan against credit-facility rules and CL covenants. RTL and Ground Up loans only",
    details:
      "Evaluates the facility's DB and CL covenant checks (LTAIV / ILTC / LTARV, mortgagor equity, 3-mo debt service) that supersede product guidelines. RTL and Ground Up only — DSCR loans aren't in this facility.",
    outcomes: [
      "Flags anything that fails or couldn't be checked",
      "Approving creates an exception request, not a condition",
    ],
  },
  {
    key: "fieldDiff",
    statusKey: "field_diff",
    pipeline: "preclose",
    name: "Field Difference Review",
    summary:
      "Compares extracted document values against what's entered on the loan.",
    details:
      "Flags material differences between document extracts and the entered loan (sizer economics, appraisal values, credit FICO, entity legal name). Differences stage as findings below, routable to a person to validate.",
    outcomes: [
      "Flags mismatches showing both values",
      "Each difference can be assigned to someone to check",
    ],
  },
  // Post-UW QC and Index Unexecuted Package stay last, in this order.
  {
    key: "postUwQc",
    statusKey: "post_uw_qc",
    pipeline: "preclose",
    name: "Post-UW QC",
    summary:
      "Runs the QC checklist — expirations, missing docs, and terms tie-out.",
    details:
      "Runs the QC-checklist gate, document-expiration checks, and the Note–HUD terms tie-out. Findings appear in the AI Conditions / QC review queue when it finishes.",
    outcomes: [
      "Confirms the Note and HUD match the loan terms",
      "Adds anything wrong to the review list",
      "Run Index Unexecuted Package first",
    ],
  },
  {
    key: "unexecuted",
    statusKey: "unexecuted_package",
    pipeline: "preclose",
    name: "Index Unexecuted Package",
    summary: "Splits the closing package into separate documents on the loan.",
    details:
      "Splits the unexecuted loan package into child documents and extracts drafted Note and HUD terms so the terms tie-out has drafts to reconcile.",
    outcomes: [
      "Extracts draft Note and HUD terms",
      "Adds nothing to review — prepares the file for Post-UW QC",
    ],
  },
  {
    key: "executed",
    statusKey: "executed_package",
    pipeline: "preclose",
    name: "Split Executed Package",
    summary: "Splits the signed closing scan into separate documents on the loan.",
    details:
      "Day-of-funding release review: splits the executed (signed) closing-package scan into child documents — signature blocks included — and extracts them, so the executed terms are on file for the funding decision.",
    outcomes: [
      "Splits the signed closing package into separate documents",
      "Extracts the executed Note, HUD and related terms",
      "Adds nothing to review — prepares the executed file for funding",
    ],
  },

  // ── Acquisitions pipeline (correspondent loans we're BUYING) ──────────────
  {
    key: "acquisitionReview",
    statusKey: "acquisition_review",
    pipeline: "acquisitions",
    name: "Acquisition Review (full)",
    summary: "Runs the whole post-close review in order, end to end",
    details:
      "The composed review: extracts every document, populates the loan from those extracts, validates document presence against the derived task list, indexes the collateral (executed-package split + Sourcepoint), then runs the deep closed-package checks. Nothing is gated out — a missing executed package is reported as a phase outcome rather than stopping the run. The individual buttons below still work on their own; this is the one that runs them in the right order.",
    outcomes: [
      "Populates the loan from its documents, then checks what's missing",
      "Indexes the collateral and runs the closed-package checks",
      "Reports any phase that failed instead of failing the whole run",
    ],
  },
  {
    key: "closedPackageReview",
    statusKey: "closed_package_review",
    pipeline: "acquisitions",
    name: "Closed Package Review",
    summary: "Validate the underwrite against the loan as closed",
    details:
      "Treats the executed package (Note, Mortgage, Guaranty, HUD) as the source of truth and works backwards through the underwrite, validating supporting files against it. Requires an executed closing package on the loan — without one the run comes back blocked, asking for the package first.",
    outcomes: [
      "Checks the underwriting against the signed closing documents",
      "Needs the signed closing package uploaded first",
      "Returns a summary — the full review is still being built",
    ],
  },
  {
    key: "acqPrecloseReview",
    statusKey: "acq_preclose_review",
    pipeline: "acquisitions",
    name: "Pre-close Acquisition Review",
    summary: "Advisory check on a locked, not-yet-closed acquisition",
    details:
      "Checks the rate lock, open conditions and exceptions, and correspondent file coverage (sizer, credit, appraisal, title, insurance, entity docs, application). Advisory only: it never green-lights a purchase — there is no definitive pass until the loan closes and the collateral package is reviewable.",
    outcomes: [
      "Summarizes the rate lock, open conditions and missing documents",
      "Advisory only — it never approves the purchase",
    ],
  },
  {
    key: "closingReadiness",
    statusKey: "closing_readiness",
    // Pipeline-agnostic: a closing on preclose, a trade settlement on
    // acquisitions. The deterministic signal it explains is what the Closing
    // tab / calendar render; this run attributes the gating items.
    pipeline: "both",
    name: "Closing Readiness",
    summary:
      "Explains what is holding up funding — external vs internal — and who owns each item.",
    details:
      "Reads the deterministic closing-readiness signal (scheduling entries, open conditions, exceptions, documents, lock, closing documents) and attributes every gating item as external (title, borrower, counsel) or internal (UW, closer), with an owner and a next action. Its readiness call is never more optimistic than the deterministic level. Also runs on its own when the loan enters Clear To Close / Closing (or Cleared to Purchase / Funding on acquisitions).",
    outcomes: [
      "Headline + readiness call, shown on the Closing tab and the calendar card",
      "Each gating item tagged external or internal with an owner and next step",
      "Stages nothing — it explains the signal, it doesn't change the loan",
    ],
  },
  {
    key: "collateralSearch",
    statusKey: "collateral_search",
    // Pipeline-agnostic: shows on preclose AND acquisitions loans.
    pipeline: "both",
    name: "Collateral Search",
    summary: "Check whether the collateral is at Sourcepoint and imaged",
    details:
      "Queries the Sourcepoint inventory by the loan's EV/LQ id to confirm the indexed package is there and each recorded instrument has been imaged. Checks existence and imaging only — Closed Package Review is what reviews the contents.",
    outcomes: [
      "Reports whether the package is indexed and each recorded instrument imaged",
      "Lists anything missing; a closed loan with no imaged collateral raises a finding",
    ],
  },
];

// review.key → the run trigger. All three return the same queued/skipped shape.
const RUN_BY_KEY = {
  opening: losAiReviewsApi.runOpeningReview,
  postUwQc: losAiReviewsApi.runPostUwQc,
  unexecuted: losAiReviewsApi.runUnexecutedPackageIndex,
  executed: losAiReviewsApi.runExecutedPackage,
  hardExceptions: losAiReviewsApi.runHardExceptions,
  suggestConditions: losAiReviewsApi.runSuggestConditions,
  fieldDiff: losAiReviewsApi.runFieldDiff,
  closedPackageReview: losAiReviewsApi.runClosedPackageReview,
  acqPrecloseReview: losAiReviewsApi.runAcqPrecloseReview,
  collateralSearch: losAiReviewsApi.runCollateralSearch,
  acquisitionReview: losAiReviewsApi.runAcquisitionReview,
  closingReadiness: losAiReviewsApi.runClosingReadiness,
};

// Compact gate suffix for the agent-card footer meta line ("Last run … · X").
const GATE_HINT_SHORT = {
  already_run: "once per loan",
  no_changes: "no changes since",
};

// A blocked run comes back 200 with a skipped marker (new shape or the older
// opening-specific `{ skipped: … }`), NOT the 202 queued envelope.
const isSkipped = (r) => r?.status === "skipped" || !!r?.skipped;

// The backend's in-flight run lock, read off a /status entry. `reason` is checked
// too so an older/newer payload that only carries one of the two still reads as
// running. This is what "running" means everywhere below — the transient
// in-session flag only covers the sliver before the POST comes back.
const isServerRunning = (backend) =>
  !!backend?.running || backend?.reason === "running";

// "started 8/25 4:07 PM by dev1@…" — the who/when off the lock, for tooltips.
const runStartedBy = (run) => {
  if (!run) return null;
  const when = run.startedAt ? formatDateTime(run.startedAt) : null;
  const who = run.startedBy;
  if (when && who) return `Started ${when} by ${who}`;
  if (when) return `Started ${when}`;
  if (who) return `Started by ${who}`;
  return null;
};

const RunButton = ({ running, disabled, hasRun, onClick }) => {
  // Per the theme button rule, whenever we set `color` we set `bgcolor` in the
  // same block (default AND hover). Running keeps the purple "active" look;
  // gated-off (disabled but not running) reads as muted/unavailable.
  const disabledSx = running
    ? { color: "#7c3aed", bgcolor: "#f8f6ff", borderColor: "#e6e0fb" }
    : { color: "#94a3b8", bgcolor: "#f8fafc", borderColor: "#e8edf2" };

  const sx = {
    textTransform: "none",
    minWidth: 0,
    gap: 0.75,
    px: 1.75,
    py: 0.875,
    fontSize: 12.5,
    fontWeight: 600,
    borderRadius: "8px",
    boxShadow: "0 1px 1.5px rgba(15,23,42,0.05)",
    bgcolor: "#fff",
    color: "#1e293b",
    border: "1px solid #dbe1e8",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: "#fdfcff", color: "#6d28d9", borderColor: "#c7b8f5" },
    "&.Mui-disabled": disabledSx,
  };

  return (
    <Button
      variant="outlined"
      size="small"
      disableElevation
      disabled={disabled}
      onClick={onClick}
      startIcon={
        running ? (
          <CircularProgress size={12} thickness={5} sx={{ color: "inherit" }} />
        ) : hasRun ? (
          <CheckRounded sx={{ fontSize: 15 }} />
        ) : (
          <PlayArrowRounded sx={{ fontSize: 16 }} />
        )
      }
      sx={sx}
    >
      {running ? "Running" : hasRun ? "Run again" : "Run"}
    </Button>
  );
};

// Small circular status marker for the agent card: spinner (running) / green
// check (has run) / hollow gray dot (never run).
const StatusCircle = ({ running, hasRun }) => {
  const base = {
    width: 22,
    height: 22,
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
  if (running) {
    return (
      <Box sx={{ ...base, bgcolor: "#f5f3ff", color: "#7c3aed" }}>
        <CircularProgress size={12} thickness={5} sx={{ color: "inherit" }} />
      </Box>
    );
  }
  if (hasRun) {
    return (
      <Box sx={{ ...base, bgcolor: "#dcfce7", color: "#16a34a" }}>
        <CheckRounded sx={{ fontSize: 14 }} />
      </Box>
    );
  }
  return (
    <Box sx={{ ...base, bgcolor: "#eef1f5" }}>
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "999px", bgcolor: "#94a3b8" }}
      />
    </Box>
  );
};

// Footer meta text for an agent card. A server-side in-flight run wins (it's the
// authoritative state and may belong to another user), then the in-session
// action, then the backend last-run (+ compact gate suffix), else "Not run yet".
const footerMeta = ({ transient, backend, gatedOff, serverRunning }) => {
  if (serverRunning) {
    const startedAt = backend?.run?.startedAt;
    return startedAt
      ? `Running since ${formatDateTime(startedAt)}`
      : "Running — you'll be notified";
  }
  if (transient === "running") return "Starting…";
  if (transient === "launched") return "Queued — you'll be notified";
  if (backend?.lastRun) {
    const suffix = gatedOff && GATE_HINT_SHORT[backend.reason];
    return `Last run ${formatDateTime(backend.lastRun)}${suffix ? ` · ${suffix}` : ""}`;
  }
  return "Not run yet";
};

// One compact agent card in the AI Reviews grid.
const AgentCard = ({ review, transient, backend, onRun, onForce }) => {
  // Server lock first (survives refresh, visible to everyone), then our own
  // in-flight POST.
  const serverRunning = isServerRunning(backend);
  const running = transient === "running" || serverRunning;
  const hasRun = transient === "launched" || !!backend?.lastRun;
  const gatedOff = backend?.canRun === false;
  const pending = !hasRun && !running;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.875,
        p: "12px 14px",
        borderRadius: "10px",
        border: pending ? "1px dashed #dbe1e8" : "1px solid #e5e8ee",
        bgcolor: running ? "#fcfbff" : pending ? "#fcfcfd" : "#fff",
        transition: "border-color 0.12s ease, box-shadow 0.12s ease",
        "&:hover": {
          borderColor: "#d6dae0",
          boxShadow: "0 2px 8px rgba(20,30,45,0.05)",
        },
      }}
    >
      {/* Top: status + name */}
      <Flx ac sx={{ gap: 1.25, minWidth: 0 }}>
        <StatusCircle running={running} hasRun={hasRun} />
        <Txt
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: "#0f172a",
            flex: 1,
            minWidth: 0,
          }}
        >
          {review.name}
        </Txt>
      </Flx>

      {/* Description (clamped to keep cards even) */}
      <Txt
        title={review.details}
        sx={{
          fontSize: 12,
          color: "#64748b",
          lineHeight: 1.5,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {review.details}
      </Txt>

      {/* Footer: meta + run */}
      <Flx ac jb sx={{ gap: 1, mt: "2px" }}>
        <Txt
          title={
            (serverRunning && runStartedBy(backend?.run)) ||
            undefined
          }
          sx={{
            fontSize: 11.5,
            color: "#94a3b8",
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {footerMeta({ transient, backend, gatedOff, serverRunning })}
        </Txt>
        <Flx column sx={{ alignItems: "flex-end", gap: "2px", flexShrink: 0 }}>
          <RunButton
            running={running}
            disabled={running || gatedOff}
            hasRun={hasRun}
            onClick={onRun}
          />
          {gatedOff && !running && (
            // Deliberate admin override — bypasses the already_run / no_changes
            // gate. Low-emphasis text action so the normal path stays gated.
            <Button
              size="small"
              onClick={onForce}
              sx={{
                textTransform: "none",
                minWidth: 0,
                p: 0,
                fontSize: 10.5,
                fontWeight: 600,
                lineHeight: 1.4,
                color: "#7c3aed",
                bgcolor: "transparent",
                "&:hover": {
                  color: "#6d28d9",
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Force re-run
            </Button>
          )}
        </Flx>
      </Flx>
    </Box>
  );
};

// ── Table layout ───────────────────────────────────────────────────────────
// Design tokens lifted verbatim from the "Operations table" spec (its :root
// vars). The spec's html font-size is 15px, so its rem sizes are converted at
// 15px/rem (.68rem → 10.2px, .84rem → 12.6px, …). IBM Plex Mono isn't loaded in
// this app, so `mono` keeps the spec's fallback chain.
const T = {
  ink: "#171A21",
  ink2: "#4B5563",
  muted: "#8A919E",
  border: "#E4E7EC",
  border2: "#D5D9E0",
  surface2: "#FBFCFD",
  accent: "#2E4FD8",
  accentSoft: "#EDF0FD",
  accentInk: "#22399B",
  green: "#0B7A55",
  greenSoft: "#E8F6F0",
  amber: "#9A6700",
  amberSoft: "#FBF3E0",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

// .pill — uppercase status pill. Derived only from what the status endpoint
// actually returns (lastRun / canRun / reason); nothing is inferred beyond it.
const STATUS_CHIP = {
  completed: { label: "Completed", color: T.green, bg: T.greenSoft },
  never: {
    label: "Never ran",
    color: T.muted,
    bg: T.surface2,
    border: `1px solid ${T.border}`,
  },
  gated: { label: "Up to date", color: T.amber, bg: T.amberSoft },
  blocked: {
    label: "Blocked",
    color: T.muted,
    bg: T.surface2,
    border: `1px dashed ${T.border2}`,
  },
  running: { label: "Running", color: T.accentInk, bg: T.accentSoft },
  queued: { label: "Queued", color: T.accentInk, bg: T.accentSoft },
};

// Known gate reasons get a written-out label; anything else the backend sends is
// humanized rather than dropped, so a new reason still reads sensibly.
const REASON_LABEL = {
  already_run: "Already run once on this loan",
  no_changes: "No changes since last run",
  // The in-flight lock: canRun is false because a run hasn't finished yet, NOT
  // because the agent is gated. Force doesn't override it — running is running.
  running: "A run is already in progress",
  // Collateral Search probes Sourcepoint (external state that changes without any
  // loan edit), so it is never gated — always re-runnable.
  external_check: "Re-runnable — checks external Sourcepoint state",
};
// review.key → the exact `sourceAgent` string(s) that button's findings carry,
// confirmed from the backend emitters (AGENT_NAME in each service):
//   hard_exception_checker  loan_review/hard_exception_checker.py
//   field_diff_agent        loan_review/field_diff_agent.py
//   post_uw_qc              workflows/stages/post_uw_qc.py
//   closed_package_review   loan_review/closed_package_checks.py
// Suggested Conditions is the emitter RUNNER (missing-doc + per-extract + the
// open-ended cross-doc specialists), so it's handled as a remainder below rather
// than a fixed list — it owns every finding no other button claims. Agents not
// listed here (Loan Opening, Index, Pre-close Acq) stage no findings.
const CLAIMED_SOURCE_AGENTS = {
  hardExceptions: ["hard_exception_checker"],
  fieldDiff: ["field_diff_agent"],
  postUwQc: ["post_uw_qc"],
  closedPackageReview: ["closed_package_review"],
};

// sourceAgents owned by a specific button, so the Suggested-Conditions remainder
// (everything else) doesn't double-count them. Deliberately NOT excluding any
// other value here: the AI Findings queue counts every staged finding, so the
// remainder must too, or the per-agent counts won't sum back to the queue total.
const NON_SUGGEST_SOURCE_AGENTS = new Set(
  Object.values(CLAIMED_SOURCE_AGENTS).flat(),
);

const sumStats = (entries) =>
  entries.reduce(
    (acc, s) => ({
      open: acc.open + (s?.open || 0),
      total: acc.total + (s?.total || 0),
    }),
    { open: 0, total: 0 },
  );

// The findings this button owns, so the per-agent counts reconcile with the
// AI Findings queue total (every finding lands in exactly one button's bucket).
const findingStatsFor = (reviewKey, byAgent) => {
  if (!byAgent) return null;
  if (reviewKey === "suggestConditions") {
    // Remainder: everything not claimed by another button (emitters + cross-doc
    // specialists + any unattributed finding).
    return sumStats(
      Object.entries(byAgent)
        .filter(([a]) => !NON_SUGGEST_SOURCE_AGENTS.has(a))
        .map(([, s]) => s),
    );
  }
  const names = CLAIMED_SOURCE_AGENTS[reviewKey];
  if (!names) return null; // this agent stages no findings
  return sumStats(names.map((n) => byAgent[n]));
};

// What a run PRODUCED, for the Output column: { output, outputCount }. Loan
// Opening reports the field writes still standing; findings agents report their
// open count. Unknown → "—" rather than a guess. (The gate reason is NOT shown
// here — that's the Status chip + its tooltip.)
const outputFor = (review, produced) => {
  if (review.key === "opening") {
    return produced.writes == null
      ? { output: null, outputCount: null }
      : {
          output: produced.writes === 1 ? "field changed" : "fields changed",
          outputCount: produced.writes,
        };
  }
  const stats = findingStatsFor(review.key, produced.byAgent);
  if (!stats || stats.total === 0) return { output: null, outputCount: null };
  if (stats.open > 0) {
    return {
      output: stats.open === 1 ? "finding open" : "findings open",
      outputCount: stats.open,
    };
  }
  return { output: "All findings reviewed", outputCount: null };
};

const reasonLabel = (reason) =>
  !reason
    ? null
    : REASON_LABEL[reason] ||
      String(reason)
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase());

// .a-name + .a-desc
const AgentCell = (p) => (
  <Box sx={{ minWidth: 0, py: "12px" }}>
    <Box
      sx={{
        fontSize: "12.6px",
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: T.ink,
        lineHeight: 1.45,
      }}
    >
      {p.data?.name}
    </Box>
    {/* Short "what it is" line; the resulting actions get their own column. */}
    <Box
      title={p.data?.details}
      sx={{
        fontSize: "11.25px",
        color: T.muted,
        marginTop: "2px",
        maxWidth: "44ch",
        lineHeight: 1.45,
        whiteSpace: "normal",
      }}
    >
      {p.data?.summary}
    </Box>
    {/* Loan Opening is the only agent that writes to the loan directly — flag it
        right under the name so the "this touches loan data" warning is unmissable. */}
    {p.data?.writesLoanData && (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          mt: "7px",
          padding: "3px 9px",
          borderRadius: "99px",
          bgcolor: T.amberSoft,
          color: T.amber,
          fontSize: "10.2px",
          fontWeight: 700,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          lineHeight: 1.2,
        }}
      >
        <WarningAmberRounded sx={{ fontSize: 12, flexShrink: 0 }} />
        Immediately Updates Loan After Running
      </Box>
    )}
  </Box>
);

// "After running" — the resulting actions as short plain-language bullets, so a
// non-technical reader can see what a run does at a glance.
const OutcomesCell = (p) => {
  const items = p.data?.outcomes;
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Flx ac sx={{ height: "100%" }}>
        <Box sx={{ fontSize: "11.25px", color: T.muted }}>—</Box>
      </Flx>
    );
  }
  return (
    <Box
      component="ul"
      sx={{ minWidth: 0, py: "12px", m: 0, pl: 0, listStyle: "none" }}
    >
      {items.map((text, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            position: "relative",
            pl: "11px",
            fontSize: "11.25px",
            color: T.ink2,
            lineHeight: 1.5,
            whiteSpace: "normal",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "6.5px",
              width: "3px",
              height: "3px",
              borderRadius: "50%",
              bgcolor: T.muted,
            },
          }}
        >
          {text}
        </Box>
      ))}
    </Box>
  );
};

// What each status means, and for a gated/blocked agent WHY it won't run. The
// gate reasons come from the backend (`reason` on the status payload).
const STATUS_HELP = {
  completed: "This agent has run on this loan. You can run it again.",
  never: "This agent hasn't been run on this loan yet.",
  running:
    "This agent is currently running — you'll be notified when it finishes.",
  queued:
    "Queued and running in the background. You'll be notified when it finishes.",
};
const GATE_HELP = {
  already_run:
    "Gated: this agent runs once per loan and has already run. Use Force re-run to run it again anyway.",
  no_changes:
    "Gated: the loan hasn't changed since the last run, so re-running would produce the same result. Use Force re-run to run it again anyway.",
};
const statusHelp = (row) => {
  if (!row) return "";
  if (row.status === "running") {
    // Who/when comes off the server lock, so a run someone ELSE started reads as
    // theirs rather than looking like a stuck button.
    const started = runStartedBy(row.run);
    return started
      ? `${STATUS_HELP.running} ${started}. Force re-run can't override a run in progress.`
      : STATUS_HELP.running;
  }
  if (row.status === "gated") {
    return (
      GATE_HELP[row.reason] ||
      "Gated: this agent can't be re-run right now. Use Force re-run to override."
    );
  }
  if (row.status === "blocked") {
    const why = reasonLabel(row.reason);
    return why
      ? `Blocked: ${why}. This agent can't run until that's resolved.`
      : "Blocked: this agent can't run on this loan right now.";
  }
  return STATUS_HELP[row.status] || "";
};

// Relative "x days ago" off the last-run timestamp (normalized to a UTC instant
// so a naive backend ISO doesn't drift a day). "today" for < 1 day.
const daysAgoLabel = (iso) => {
  try {
    const then = new Date(normalizeIsoStringToUtc(iso));
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (Number.isNaN(days)) return "";
    if (days <= 0) return "today";
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } catch {
    return "";
  }
};

// Combined Status + Last run. Ran states lead with the run date and a color:
//   • gated ("up to date") → amber "Completed · Last ran <date>"
//   • completed (clean)    → green "Last ran <date>"
// with "(x days ago)" underneath. Never/blocked/running/queued keep a word pill.
const StatusCell = (p) => {
  const row = p.data;
  const status = row?.status;
  const s = STATUS_CHIP[status];
  if (!s) {
    return (
      <Flx ac jc sx={{ height: "100%" }}>
        <Tooltip title="Run status is unavailable — the status endpoint couldn't be reached.">
          <Box sx={{ fontSize: "12.6px", color: T.muted, cursor: "help" }}>
            —
          </Box>
        </Tooltip>
      </Flx>
    );
  }

  // Ran states keep the SAME colored pill (amber = up-to-date, green = clean),
  // now carrying the run date; "(x days ago)" sits under it.
  const isRan =
    (status === "gated" || status === "completed") && !!row.lastRun;
  const label = isRan
    ? status === "gated"
      ? `Completed · Last ran ${formatDateTime(row.lastRun)}`
      : `Last ran ${formatDateTime(row.lastRun)}`
    : s.label;
  const rel = isRan ? daysAgoLabel(row.lastRun) : "";

  // Everything vertically centered in the (auto-height) row.
  return (
    <Flx
      column
      sx={{
        height: "100%",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "3px",
        minWidth: 0,
        py: "10px",
      }}
    >
      <Tooltip title={statusHelp(row)} placement="top" arrow>
        <Box
          component="span"
          sx={{
            cursor: "help",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "10.2px",
            fontWeight: 700,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            padding: "3px 9px",
            borderRadius: "99px",
            color: s.color,
            bgcolor: s.bg,
            border: s.border || "none",
            whiteSpace: "normal",
            lineHeight: 1.3,
          }}
        >
          {row?.running && (
            <CircularProgress
              size={9}
              thickness={6}
              sx={{ color: "inherit", flexShrink: 0 }}
            />
          )}
          {label}
        </Box>
      </Tooltip>
      {rel && <Box sx={{ fontSize: "10.5px", color: T.muted }}>({rel})</Box>}
    </Flx>
  );
};

// .a-output — WHAT THE RUN PRODUCED (field changes / open findings), with the
// count led in accent. Not the gate reason: that's the Status chip + tooltip.
const OutputCell = (p) => (
  <Flx ac sx={{ height: "100%", minWidth: 0 }}>
    <Box
      title={
        p.data?.output
          ? `${p.data.outputCount ?? ""} ${p.data.output}`.trim()
          : undefined
      }
      sx={{
        fontSize: "11.4px",
        color: T.ink2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {p.data?.outputCount != null && (
        <Box
          component="span"
          sx={{ color: T.accent, fontWeight: 600, mr: "4px" }}
        >
          {p.data.outputCount}
        </Box>
      )}
      {p.data?.output || "—"}
    </Box>
  </Flx>
);

// .btn.btn-run — dark solid; sets bgcolor and color together (theme button rule).
const TableRunButton = ({ running, disabled, onClick }) => (
  <Button
    size="small"
    disableElevation
    disabled={disabled}
    onClick={onClick}
    startIcon={
      running ? (
        <CircularProgress size={11} thickness={5} sx={{ color: "inherit" }} />
      ) : (
        <PlayArrowRounded sx={{ fontSize: 15 }} />
      )
    }
    sx={{
      "& .MuiButton-startIcon": { marginRight: "5px", marginLeft: "-2px" },
      textTransform: "none",
      minWidth: 0,
      fontSize: "11.7px",
      fontWeight: 600,
      padding: "6px 13px",
      borderRadius: "7px",
      lineHeight: 1.45,
      bgcolor: T.ink,
      color: "#fff",
      whiteSpace: "nowrap",
      "&:hover": { bgcolor: "#2B2F3A", color: "#fff" },
      "&.Mui-disabled": {
        bgcolor: T.border,
        color: T.muted,
        cursor: "not-allowed",
        pointerEvents: "auto",
      },
    }}
  >
    {running ? "Running" : "Run"}
  </Button>
);

const ActionCell = (p) => {
  const { running, gatedOff, status, review } = p.data || {};
  // A known gate (already_run / no_changes) offers the deliberate admin
  // override; anything else that blocks the run just shows a disabled Run.
  const forceable = gatedOff && status === "gated" && !running;
  return (
    <Flx ac sx={{ height: "100%", justifyContent: "flex-start" }}>
      {forceable ? (
        // .link-btn
        <Button
          variant="text"
          size="small"
          onClick={() => p.context?.onForce?.(review)}
          sx={{
            textTransform: "none",
            minWidth: 0,
            padding: 0,
            fontSize: "11.4px",
            fontWeight: 600,
            color: T.accent,
            bgcolor: "transparent",
            whiteSpace: "nowrap",
            "&:hover": {
              color: T.accent,
              bgcolor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          Force re-run
        </Button>
      ) : (
        <TableRunButton
          running={running}
          disabled={running || gatedOff}
          onClick={() => p.context?.onRun?.(review)}
        />
      )}
    </Flx>
  );
};

const AiReviewsPanel = ({ onCount }) => {
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;

  // Transient in-session status per review: idle | running (POST in flight) |
  // launched (queued to the listener). Backend gating/history lives in
  // `statusData` (keyed by review.key from GET /status).
  const [runs, setRuns] = useState(() =>
    Object.fromEntries(REVIEWS.map((r) => [r.key, "idle"])),
  );
  const [statusData, setStatusData] = useState({});
  const [expanded, setExpanded] = useState(true); // primary control — open by default
  // "Something is running" for the header pill AND the status poll below. Counts
  // the server lock (any user's run, survives refresh) as well as our own
  // in-flight POST.
  const anyRunning = useMemo(
    () =>
      Object.values(runs).some((s) => s === "running") ||
      Object.values(statusData).some(isServerRunning),
    [runs, statusData],
  );

  // Agent set is PER PIPELINE: origination agents must not show on acquisitions
  // loans (they're origination-oriented and some write only to the preclose
  // collection), and vice versa. `pipeline: "both"` (e.g. Collateral Search)
  // shows on either. The status response tags each agent with `pipeline` and is
  // authoritative; fall back to our static tag when status hasn't loaded / the
  // endpoint is unreachable.
  const isAcquisitions = useIsAcquisitions();
  const loanPipeline = isAcquisitions ? "acquisitions" : "preclose";
  const visibleReviews = useMemo(
    () =>
      REVIEWS.filter((r) => {
        const p = statusData[r.key]?.pipeline || r.pipeline;
        return p === "both" || p === loanPipeline;
      }),
    [statusData, loanPipeline],
  );

  const setTransient = useCallback(
    (key, status) => setRuns((s) => ({ ...s, [key]: status })),
    [],
  );

  // Pull per-agent gating + last-run from the backend and key it by review.key.
  const fetchStatus = useCallback(async () => {
    if (!dealId) return;
    try {
      const data = await losAiReviewsApi.getStatus(dealId);
      setStatusData({
        opening: data?.loan_opening,
        unexecuted: data?.unexecuted_package,
        executed: data?.executed_package,
        postUwQc: data?.post_uw_qc,
        hardExceptions: data?.hard_exceptions,
        acquisitionReview: data?.acquisition_review,
        closedPackageReview: data?.closed_package_review,
        acqPrecloseReview: data?.acq_preclose_review,
        collateralSearch: data?.collateral_search,
        suggestConditions: data?.suggest_conditions,
        fieldDiff: data?.field_diff,
      });
    } catch {
      // Endpoint unreachable — leave status unknown so buttons stay enabled
      // (a run attempt will surface its own error).
    }
  }, [dealId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ── Releasing the in-flight lock in the UI ────────────────────────────────
  // The lock clears server-side when the completion record lands, so the button
  // only comes back on a REFETCH. Two triggers, because neither covers the other:
  //
  //  1. The `*_complete` notification for this loan (the documented trigger).
  //     Rides the realtime socket into the notifications slice, and the polling
  //     fallback hydrates the same slice when Web PubSub is dark. `lastRun` can
  //     still lag the notification by a beat — hence the short re-check.
  //  2. A poll while anything is running. The run may have been started by
  //     ANOTHER user, and their completion notification is never addressed to us,
  //     so #1 would never fire and the row would sit on "Running" until the
  //     20-minute stale ceiling.
  const latestCompletion = useSelector((state) => {
    const items = state.notifications?.items;
    if (!Array.isArray(items) || !dealId) return null;
    const hit = items.find(
      (n) => n?.dealId === dealId && String(n?.type || "").endsWith("_complete"),
    );
    return hit ? hit._id : null; // newest-first list → first match is the newest
  });

  useEffect(() => {
    if (!latestCompletion) return undefined;
    fetchStatus();
    // One short re-check: the completion notification can beat the status write.
    const t = setTimeout(fetchStatus, 2500);
    return () => clearTimeout(t);
  }, [latestCompletion, fetchStatus]);

  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(fetchStatus, 15000);
    return () => clearInterval(id);
  }, [anyRunning, fetchStatus]);

  // What each run produced, for the Output column. Same endpoints the Field
  // Changes / Findings panels below use, so the numbers always agree with them.
  const [produced, setProduced] = useState({ writes: null, byAgent: null });
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      const [writes, findings] = await Promise.all([
        losAiReviewsApi.getFieldWrites(dealId).catch(() => null),
        losAiReviewsApi.getFindings(dealId).catch(() => null),
      ]);
      if (cancelled) return;
      // Agent populations only (reversal rows carry revertOf) and only the ones
      // still standing — matching the Field Changes panel's own count.
      const writeCount = Array.isArray(writes)
        ? writes.filter((w) => !w.revertOf && !w.reverted).length
        : null;
      let byAgent = null;
      if (Array.isArray(findings)) {
        byAgent = {};
        for (const f of findings) {
          // "" bucket = no sourceAgent → falls into the Suggested-Conditions
          // remainder, so the per-agent counts still sum to the queue total.
          const k = f?.sourceAgent || "";
          if (!byAgent[k]) byAgent[k] = { total: 0, open: 0 };
          byAgent[k].total += 1;
          if (f.status === "staged") byAgent[k].open += 1;
        }
      }
      setProduced({ writes: writeCount, byAgent });
    })();
    return () => {
      cancelled = true;
    };
    // Re-pulled on a completion notification too, so the Output column reflects
    // what the finished run actually produced.
  }, [dealId, latestCompletion]);

  const run = useCallback(
    async (review, { force = false, extra = null } = {}) => {
      if (!dealId || runs[review.key] === "running") return;
      // Don't fire into the server lock — it would just come back 409, and
      // `force` doesn't override it either.
      if (isServerRunning(statusData[review.key])) return;
      setTransient(review.key, "running");
      try {
        // `force: true` is the admin override the backend added: it bypasses the
        // already_run / no_changes gate (the gate default is unchanged, so the
        // status endpoint still reports canRun accurately). `extra` carries
        // agent-specific run context (Closed Package Review's packageMode /
        // documentIds).
        const resp = await RUN_BY_KEY[review.key](dealId, {
          ...(force ? { force: true } : {}),
          ...(extra || {}),
        });
        if (isSkipped(resp)) {
          enqueueSnackbar(resp?.message || `${review.name} was skipped.`, {
            variant: "info",
          });
          setTransient(review.key, "idle");
        } else {
          // 202 queued envelope — the listener runs the agent and fires a
          // completion notification; results don't come back on this response.
          enqueueSnackbar(
            resp?.message ||
              `${review.name} queued — you'll be notified when it finishes.`,
            { variant: "success" },
          );
          setTransient(review.key, "launched");
        }
      } catch (err) {
        // 409 = the backend's in-flight run lock (already_running): not an error,
        // just "it's already running". Surface it calmly; fetchStatus() below then
        // paints the row as Running.
        const alreadyRunning = err?.response?.status === 409;
        enqueueSnackbar(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            `Could not start ${review.name}.`,
          { variant: alreadyRunning ? "info" : "error" },
        );
        setTransient(review.key, "idle");
      } finally {
        // Refresh gating/last-run after any attempt (queue or skip).
        fetchStatus();
      }
    },
    [dealId, runs, statusData, setTransient, enqueueSnackbar, fetchStatus],
  );

  // Confirm before running — these kick off real work (Loan Opening writes loan
  // data), so a Run/Force click opens a prompt instead of firing immediately.
  // { review, force, packageMode, documentIds } | null
  const [confirm, setConfirm] = useState(null);
  const requestRun = useCallback(
    (review, force = false) =>
      setConfirm({
        review,
        force,
        // Closed Package Review asks the user what it's reviewing rather than
        // guessing; default to the common case (one monolithic scan).
        packageMode: "single_scan",
        documents: [],
      }),
    [],
  );
  const confirmRun = useCallback(() => {
    if (!confirm) return;
    const { review, force, packageMode, documents } = confirm;
    setConfirm(null);
    const extra =
      review.key === "closedPackageReview"
        ? {
            packageMode,
            // Omitted entirely when nothing is picked → backend auto-detects.
            ...(documents?.length
              ? { documentIds: documents.map((d) => d.id) }
              : {}),
          }
        : null;
    run(review, { force, extra });
  }, [confirm, run]);

  const cReview = confirm?.review;
  const cIsOpening = cReview?.key === "opening";
  const cIsClosedPackage = cReview?.key === "closedPackageReview";

  // File picker for Closed Package Review — the loan's documents with the
  // Closing / Terms & Sizer groups surfaced first (per run-agent-reviews.md).
  const documentOptions = useMemo(() => {
    const docs = Array.isArray(loan?.loanDocuments) ? loan.loanDocuments : [];
    const rank = (d) => {
      const g = String(d?.docGroup || "").toLowerCase();
      if (g.includes("closing")) return 0;
      if (g.includes("terms") || g.includes("sizer")) return 1;
      return 2;
    };
    return docs
      .filter((d) => d?._id || d?.oid)
      .map((d) => ({
        id: String(d.oid || d._id),
        label: d.file_display_name || d.file_storage_name || "Document",
        group: d.docGroup || "Other",
        rank: rank(d),
      }))
      .sort(
        (a, b) => a.rank - b.rank || String(a.label).localeCompare(b.label),
      );
  }, [loan]);

  // ── Table rows: one per visible agent, with the run state derived once so the
  // cell renderers stay dumb. `status` drives both the chip and the action cell.
  const rowData = useMemo(
    () =>
      visibleReviews.map((review) => {
        const transient = runs[review.key];
        const backend = statusData[review.key];
        // A run in flight per the backend's server-side lock (reason "running",
        // running:true) — set by THIS session, another user, or the scheduler,
        // and it survives a page refresh. Counts as running just like our own
        // in-flight POST so it shows the spinner, not "Blocked".
        const backendRunning = isServerRunning(backend);
        const running = transient === "running" || backendRunning;
        const launched = transient === "launched";
        const hasRun = launched || !!backend?.lastRun;
        const gatedOff = backend?.canRun === false;
        let status;
        if (running) status = "running";
        else if (launched) status = "queued";
        else if (gatedOff)
          status = GATE_HINT_SHORT[backend?.reason] ? "gated" : "blocked";
        else if (backend?.lastRun) status = "completed";
        else if (backend) status = "never";
        else status = null; // status endpoint unreachable — say nothing
        return {
          key: review.key,
          review,
          name: review.name,
          summary: review.summary,
          details: review.details,
          outcomes: review.outcomes,
          running,
          hasRun,
          gatedOff,
          status,
          reason: backend?.reason || null,
          // The lock's { runId, startedAt, startedBy } — who/when for the Status
          // tooltip while a run is in flight.
          run: backend?.run || null,
          lastRun: backend?.lastRun || null,
          writesLoanData: !!review.writesLoanData,
          ...outputFor(review, produced),
        };
      }),
    [visibleReviews, runs, statusData, produced],
  );

  // Report "N of M agents ran" up to the section-tab badge (AI Agents).
  const ranCount = useMemo(
    () => rowData.filter((r) => r.hasRun).length,
    [rowData],
  );
  useEffect(() => {
    onCount?.({ ran: ranCount, total: rowData.length });
  }, [onCount, ranCount, rowData.length]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Agent",
        colId: "agent",
        flex: 24,
        minWidth: 195,
        autoHeight: true,
        wrapText: true,
        cellRenderer: AgentCell,
      },
      {
        headerName: "What Occurs After running",
        colId: "outcomes",
        flex: 34,
        minWidth: 260,
        autoHeight: true,
        wrapText: true,
        cellRenderer: OutcomesCell,
      },
      {
        headerName: "Status",
        colId: "status",
        flex: 20,
        minWidth: 160,
        // NOT autoHeight: the row height comes from the Agent/Outcomes columns;
        // this cell fills that height and the flex cell centers StatusCell's
        // (shorter) content vertically instead of pinning it to the top.
        cellStyle: { display: "flex", alignItems: "center", padding: "0 16px" },
        cellRenderer: StatusCell,
      },
      {
        headerName: "Output",
        colId: "output",
        flex: 15,
        minWidth: 120,
        cellRenderer: OutputCell,
      },
      {
        headerName: "",
        colId: "action",
        width: 120,
        cellRenderer: ActionCell,
      },
    ],
    [],
  );

  // Sorting/filtering are off on purpose: the agent order is meaningful (the
  // pipeline runs roughly in this sequence), so reordering would mislead. The
  // header ⋮ menu (pin / autosize / choose columns) is suppressed for the same
  // reason — this is a fixed control surface, not a grid to rearrange.
  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
    }),
    [],
  );

  const gridContext = useMemo(
    () => ({
      onRun: (review) => requestRun(review, false),
      onForce: (review) => requestRun(review, true),
    }),
    [requestRun],
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderRadius: "14px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header — collapse toggle */}
      <Flx
        ac
        onClick={() => setExpanded((v) => !v)}
        sx={{
          gap: 1.5,
          p: "18px 24px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #eef1f5" : "none",
          "&:hover": { bgcolor: "#fafbfc" },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "9px",
            background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
            border: "1px solid #e6e0fb",
            color: "#7c3aed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AutoAwesomeOutlined sx={{ fontSize: 23 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            sx={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "#0f172a",
              display: "block",
            }}
          >
            AI Reviews
          </Txt>
          <Txt
            sx={{
              fontSize: 12.5,
              color: "#64748b",
              lineHeight: 1.5,
              display: "block",
            }}
          >
            Re-run an AI agent on this loan. Reviews run in the background and
            notify you when they finish.
          </Txt>
        </Box>

        {anyRunning && (
          <Flx
            ac
            sx={{
              gap: 0.75,
              fontSize: 11.5,
              fontWeight: 600,
              color: "#7c3aed",
              bgcolor: "#f5f3ff",
              border: "1px solid #e6e0fb",
              borderRadius: "999px",
              px: 1.25,
              py: 0.5,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "999px",
                bgcolor: "#7c3aed",
                animation: `${pulse} 1.4s ease-in-out infinite`,
              }}
            />
            <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: "inherit" }}>
              Review in progress
            </Txt>
          </Flx>
        )}
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: "#94a3b8",
            flexShrink: 0,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      {/* Agents — table (current) or the previous card grid, per AGENT_LAYOUT */}
      <Collapse in={expanded} unmountOnExit>
        {AGENT_LAYOUT === "table" ? (
          // Table chrome mapped onto AG Grid from the spec's `table.agents`
          // rules: th 9px/18px uppercase muted, td 12px/18px, 1px row rules,
          // --surface-2 row hover, no vertical cell borders.
          <AgGridTableWrapper
            sx={{
              "--ag-background-color": "#FFFFFF",
              "--ag-header-background-color": "#FFFFFF",
              "--ag-row-hover-color": T.surface2,
              "--ag-border-color": T.border,
              "--ag-header-height": "34px",
              "--ag-cell-horizontal-border": "none",
              ".ag-root-wrapper": { border: "none", borderRadius: 0 },
              ".ag-header": { borderBottom: `1px solid ${T.border}` },
              ".ag-header-cell": { padding: "9px 18px" },
              ".ag-header-cell-text": {
                fontSize: "10.2px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: T.muted,
              },
              ".ag-cell": {
                padding: "0 18px",
                fontSize: "12.6px",
                color: T.ink,
                borderRight: "none",
                lineHeight: 1.45,
              },
              ".ag-row": { borderBottom: `1px solid ${T.border}` },
              ".ag-row-last": { borderBottom: "none" },
            }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              context={gridContext}
              getRowId={(p) => String(p.data.key)}
              domLayout="autoHeight"
              suppressCellFocus
            />
          </AgGridTableWrapper>
        ) : (
          <Box
            sx={{
              p: "16px 24px 20px",
              bgcolor: "#f8fafc",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 1.25,
            }}
          >
            {visibleReviews.map((review) => (
              <AgentCard
                key={review.key}
                review={review}
                transient={runs[review.key]}
                backend={statusData[review.key]}
                onRun={() => requestRun(review, false)}
                onForce={() => requestRun(review, true)}
              />
            ))}
          </Box>
        )}
      </Collapse>

      {/* Confirm-before-run — Loan Opening in particular writes loan data. */}
      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        maxWidth={cIsClosedPackage ? "sm" : "xs"}
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          Run {cReview?.name}?
        </DialogTitle>
        <DialogContent>
          {cIsOpening && (
            <Alert
              severity="warning"
              sx={{ mb: 1.5, fontSize: 12.5, py: 0.25, alignItems: "center" }}
            >
              This changes loan data.
            </Alert>
          )}
          <Txt sx={{ fontSize: 13, color: "#475569", lineHeight: 1.55 }}>
            {cIsOpening
              ? "Loan Opening reads the source documents and fills in empty loan fields, then adds a processing note. After it runs, you can review every change and undo them one at a time in the Field Changes table below."
              : `This queues ${cReview?.name} to run in the background. You'll be notified when it finishes.`}
          </Txt>

          {/* Closed Package Review: tell the agent what it's reviewing rather
              than letting it guess (run-agent-reviews.md). */}
          {cIsClosedPackage && (
            <Flx column sx={{ gap: 2, mt: 2.5 }}>
              <TextField
                select
                size="small"
                label="What is being reviewed?"
                value={confirm?.packageMode || "single_scan"}
                onChange={(e) =>
                  setConfirm((c) => ({ ...c, packageMode: e.target.value }))
                }
                InputProps={{ sx: { fontSize: 13 } }}
                InputLabelProps={{ sx: { fontSize: 13 } }}
              >
                <MenuItem value="single_scan" sx={{ fontSize: 13 }}>
                  One scanned package (the agent splits it first)
                </MenuItem>
                <MenuItem value="indexed" sx={{ fontSize: 13 }}>
                  Already split or Sourcepoint-indexed documents
                </MenuItem>
              </TextField>

              <Autocomplete
                multiple
                size="small"
                options={documentOptions}
                groupBy={(o) => o.group}
                value={confirm?.documents || []}
                onChange={(_, v) => setConfirm((c) => ({ ...c, documents: v }))}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Files to examine (optional)"
                    helperText="Leave empty to let the agent auto-detect the package."
                    InputProps={{ ...params.InputProps, sx: { fontSize: 13 } }}
                    InputLabelProps={{ sx: { fontSize: 13 } }}
                  />
                )}
              />
            </Flx>
          )}

          {confirm?.force && (
            <Txt
              sx={{
                fontSize: 12.5,
                color: "#b45309",
                lineHeight: 1.5,
                display: "block",
                mt: 1.5,
              }}
            >
              This is a forced re-run — it bypasses the run gate (already-run /
              no-changes).
            </Txt>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmRun}>
            {confirm?.force ? "Force re-run" : "Run"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiReviewsPanel;
